import {
  Control,
  UseFormRegister,
  UseFormReturn,
  UseFormSetValue,
} from "react-hook-form";

import { SelectOptionTypes } from "../../../../components/Input";
import { Accounts } from "../../../../services/accounts/types";
import { TransactionType } from "../../../../utils";
import { Category, PanelAction } from "../../types";

export interface CreateTransactionsProps {
  title: string;
  action: PanelAction;
  isOpen: boolean;
  onClose: () => void;
  incomeCategoryList: Category[];
  expenseCategoryList: Category[];
  transaction?: CreateTransactionsObject;
  handleOnSubmit: (data: CreateTransactionsObject) => Promise<void>;
  defaultAccount: SelectOptionTypes | null;
}

export interface TransactionFieldsProps {
  action: PanelAction;
  transaction?: CreateTransactionsObject;
  originalDate?: string;
  control: Control<CreateTransactionsObject>;
  register: UseFormRegister<CreateTransactionsObject>;
  setValue: UseFormSetValue<CreateTransactionsObject>;
  isSubmitting: boolean;
  validationRules: Record<string, unknown>;
  typeOptions: SelectOptionTypes[];
  categoryOptions: Category[];
  subCategoryOptions: SelectOptionTypes[];
  accountOptions: SelectOptionTypes[];
  loanToAccountOptions: SelectOptionTypes[];
  frequencyOptions: SelectOptionTypes[];
  shouldShowPrincipalAmount: boolean;
  shouldShowTax: boolean;
  shouldShowTaxSwitch: boolean;
  isTaxSwitchDisabled: boolean;
  taxSwitchHelperText: string;
  shouldShowCategory: boolean;
  shouldShowSubCategory: boolean;
  shouldShowFromAccount: boolean;
  shouldShowToAccount: boolean;
  isRecurringDisabled: boolean;
  recurringHelperText: string;
  isRecurring: boolean;
  maxTransactionDate?: Date;
  cashbackEarnedLabel?: string;
  cashbackEarnedHelper?: string;
  cashbackPrefillError?: string;
  isCashbackPrefillPending?: boolean;
}

export interface PendingCashbackPrefill {
  key: string;
  value: string | null | undefined;
}

export interface UseCashbackFormParams {
  form: UseFormReturn<CreateTransactionsObject>;
  accounts: Accounts[] | undefined;
  action: PanelAction;
  original?: CreateTransactionsObject;
}

export interface UseCashbackPrefillReturn {
  error: string;
  isPending: boolean;
}

export interface UseCashbackEarnedReturn extends UseCashbackPrefillReturn {
  cashbackEarnedLabel?: string;
  cashbackEarnedHelper?: string;
}

export type CashbackPrefillValue = CreateTransactionsObject["cashbackEarned"];

export interface UseCashbackPrefillParams {
  form: UseFormReturn<CreateTransactionsObject>;
  action: PanelAction;
}

export interface CashbackPrefillRequest<R> {
  key: string;
  load: () => Promise<R>;
  apply: (result: R) => void;
  errorMessage: string;
  delayMs?: number;
}

export type UseCashbackFormReturn = Required<
  Pick<
    TransactionFieldsProps,
    "cashbackPrefillError" | "isCashbackPrefillPending"
  >
> &
  Pick<TransactionFieldsProps, "cashbackEarnedLabel" | "cashbackEarnedHelper">;

export interface TypeSegmentsProps {
  options: SelectOptionTypes[];
  selectedValue?: string;
  disabled: boolean;
  onChange: (option: SelectOptionTypes) => void;
}

export interface DuplicateBannerProps {
  originalDate?: string;
}

export interface CreateTransactionsObject {
  id?: number;
  fromAccount: SelectOptionTypes | null;
  toAccount: SelectOptionTypes | null;
  amount: string;
  taxWithheld?: string | null;
  hasTaxWithheld?: boolean;
  cashbackEarned?: string | null;
  isCashbackCredited?: boolean;
  transactionType: SelectOptionTypes;
  note?: string;
  transactionDate: string | Date;
  principalAmount?: string;
  interestAmount?: number;
  category: SelectOptionTypes | null;
  subCategory: SelectOptionTypes | null;
  isRecurring: boolean;
  frequency: SelectOptionTypes | null;
  interval?: number;
  stopAfterOccurrences?: number;
}

export interface CreateTransactionsPayload {
  fromAccountId?: number;
  toAccountId?: number;
  amount?: number;
  grossAmount?: number;
  taxWithheld?: number | null;
  cashbackEarned?: number | null;
  transactionType: TransactionType;
  note?: string;
  transactionDate: string;
  principalAmount?: number;
  interestAmount?: number;
  categoryId?: number;
  subCategoryId?: number;
  isRecurring: boolean;
  frequency?: string;
  interval?: number;
  stopAfterOccurrences?: number;
}

export enum TransactionTypeOptions {
  INCOME = "Income",
  EXPENSE = "Expense",
  LOAN_PAYMENT = "Loan Payment",
  TRANSFER = "Transfer",
  TRANSFER_IN = "Transfer In",
  TRANSFER_OUT = "Transfer Out",
  MARKET_UPDATE = "Market Update",
}

export enum Frequency {
  DAILY = "Daily",
  WEEKLY = "Weekly",
  MONTHLY = "Monthly",
  ANNUALLY = "Annually",
}

export const FREQUENCY_OPTIONS = [
  { label: "Day(s)", value: Frequency.DAILY },
  { label: "Week(s)", value: Frequency.WEEKLY },
  { label: "Month(s)", value: Frequency.MONTHLY },
  { label: "Year(s)", value: Frequency.ANNUALLY },
] as SelectOptionTypes[];

export const getFrequencyOption = (frequency?: string) => {
  if (!frequency) return null;
  return FREQUENCY_OPTIONS.find((option) => option.value === frequency) || null;
};

export const TRANSACTION_TYPE_MAP: { [key: string]: TransactionType } = {
  [TransactionTypeOptions.INCOME]: TransactionType.INCOME,
  [TransactionTypeOptions.EXPENSE]: TransactionType.EXPENSE,
  [TransactionTypeOptions.LOAN_PAYMENT]: TransactionType.LOAN_PAYMENT,
  [TransactionTypeOptions.TRANSFER]: TransactionType.TRANSFER,
  [TransactionTypeOptions.TRANSFER_IN]: TransactionType.TRANSFER,
  [TransactionTypeOptions.TRANSFER_OUT]: TransactionType.TRANSFER,
};

export const TRANSACTION_OPTIONS_TYPE_MAP: {
  [key: string]: TransactionTypeOptions;
} = {
  [TransactionType.INCOME]: TransactionTypeOptions.INCOME,
  [TransactionType.EXPENSE]: TransactionTypeOptions.EXPENSE,
  [TransactionType.TRANSFER]: TransactionTypeOptions.TRANSFER,
  [TransactionType.MARKET_UPDATE]: TransactionTypeOptions.MARKET_UPDATE,
  [TransactionType.LOAN_PAYMENT]: TransactionTypeOptions.LOAN_PAYMENT,
};
